import { CustomerType, PaymentMethod } from '@repo/common';
import {
    Button,
    Card,
    Input,
    Label,
    RadioGroup,
    RadioGroupItem,
    Textarea,
} from '@repo/shadcn';
import { useEffect, useRef, useState } from 'react';
import { BsCash } from 'react-icons/bs';
import { FaCreditCard, FaPlus } from 'react-icons/fa';
import { IoMdPhonePortrait } from 'react-icons/io';

import { MODAL_ROUTE } from '@/constants';
import { CartInfo } from '@/features/slice/cart/cart.schema';
import { openDiscount } from '@/features/slice/event/event-slice.ts';
import { useModal } from '@/hooks';
import { formatCurrencyInBDT } from '@/utils';

import useFieldFocus from '../../../../hooks/use-field-focus';
import { useCreateSale } from './create-sale.hook';
import { Discount } from './discount';
import { SaleCustomerAutoComplete } from './sale-customer-autocomplete';

const PAYMENT_METHODS = [
    {
        id: PaymentMethod.CASH,
        name: 'Cash',
        icon: BsCash,
    },
    {
        id: PaymentMethod.BANK,
        name: 'Bank',
        icon: FaCreditCard,
    },
    {
        id: PaymentMethod.MobileBankingBkash,
        name: 'Bkash',
        icon: IoMdPhonePortrait,
    },

    {
        id: PaymentMethod.MobileBankingNagad,
        name: 'Nagad',
        icon: IoMdPhonePortrait,
    },
    {
        id: PaymentMethod.MobileBankingRocket,
        name: 'Rocket',
        icon: IoMdPhonePortrait,
    },
];

function CartAmount({ cartDetails }: { cartDetails: CartInfo }) {
    const {
        changeSelectedCustomer,
        singleCustomerDetails,
        selectedCustomerId,
        customerType,
        handleSubmitOrder,
        handleDiscountChange,
        discount,
        dispatch,
        paidValue,
        discountOpen,
        cashBack,
        handlePaidChange,
        selectedPaymentMethod,
        setSelectedPaymentMethod,
        transactionId,
        setTransactionId,
    } = useCreateSale();
    const discountRef = useRef<HTMLInputElement>(null);

    const receivedAmountRef = useRef<HTMLInputElement>(null);
    useFieldFocus({ d: discountRef, r: receivedAmountRef });

    const handleBlur = (field: string, value: number) => {
        if (field === 'paidAmount') {
            console.info(`Paid amount updated to: ${value}`);
        }
    };
    // const onKeyDown = () => {
    //   dispatch(openDiscount(!openDiscount));
    // };
    //

    const renderCustomerSelection = (
        customerType: CustomerType = CustomerType.WALKIN,
    ) => {
        switch (customerType) {
            case CustomerType.WHOLESALE:
                return (
                    <CustomerInfo
                        changeSelectedCustomer={changeSelectedCustomer}
                        singleCustomerDetails={singleCustomerDetails}
                        selectedCustomerId={selectedCustomerId}
                    />
                );
            case CustomerType.RETAIL:
                return (
                    <CustomerInfo
                        changeSelectedCustomer={changeSelectedCustomer}
                        singleCustomerDetails={singleCustomerDetails}
                        selectedCustomerId={selectedCustomerId}
                    />
                );

            case CustomerType.WALKIN:
                return <Textarea placeholder="Note" />;

            default:
                return null;
        }
    };

    return (
        <>
            <Card
                className="sticky top-16 box-border h-fit w-full border-l-[1px] bg-white p-6"
                id="sale"
            >
                <div className="text-sm">
                    <div className="grid gap-3">
                        <div className="text-[22px] font-semibold">Summary</div>

                        <div
                            data-orientation="horizontal"
                            role="none"
                            className="my-2 h-[1px] w-full shrink-0 bg-border"
                        ></div>
                        <ul className="grid gap-3 space-y-2">
                            <li className="flex items-center justify-between">
                                <span className="text-[16px] text-muted-foreground">
                                    Subtotal
                                </span>
                                <span className="text-[22px] text-primary">
                                    {' '}
                                    {formatCurrencyInBDT(
                                        cartDetails.sub_total_price,
                                    )}
                                </span>
                            </li>
                            <li className="flex items-center justify-between">
                                <span className="text-[16px] text-muted-foreground">
                                    Discount
                                </span>

                                <div className="flex items-center gap-2">
                                    {cartDetails.sub_total_price > 0 ? (
                                        <Discount
                                            handleDiscountChange={
                                                handleDiscountChange
                                            }
                                            discount={discount}
                                            // onKeyDown={onKeyDown}
                                        />
                                    ) : (
                                        <span
                                            className="cursor-pointer text-[22px] text-primary"
                                            onClick={() =>
                                                dispatch(
                                                    openDiscount(!discountOpen),
                                                )
                                            }
                                        >
                                            {discount.value}{' '}
                                            {discount.type == 'percentage'
                                                ? '%'
                                                : 'Tk'}
                                        </span>
                                    )}
                                </div>
                            </li>

                            <li className="flex items-center justify-between">
                                <span className="text-[16px] text-muted-foreground">
                                    Vat
                                </span>
                                <span className="text-[22px] text-primary">
                                    {formatCurrencyInBDT(cartDetails.tax)}
                                </span>
                            </li>
                            <li className="flex items-center justify-between">
                                <span className="text-[16px] text-muted-foreground">
                                    Total{' '}
                                </span>
                                <span className="text-[26px] font-semibold text-primary">
                                    {formatCurrencyInBDT(
                                        cartDetails.total_price,
                                    )}
                                </span>{' '}
                            </li>
                            <li className="flex items-center justify-between font-semibold">
                                <span className="text-[16px] text-muted-foreground">
                                    Received Amount
                                </span>

                                <EditableField
                                    inputRef={receivedAmountRef}
                                    label="Received Amount"
                                    value={Number(paidValue ?? 0)}
                                    onChange={(val: any) =>
                                        handlePaidChange(val)
                                    }
                                    onBlur={() =>
                                        handleBlur(
                                            'paidAmount',
                                            Number(paidValue),
                                        )
                                    }
                                />
                            </li>
                            <li className="flex items-center justify-between">
                                <span
                                    className={`${Number(cashBack) < 0 ? 'text-[16px] text-red-500' : 'text-[16px] text-muted-foreground'}`}
                                >
                                    {Number(cashBack) < 0
                                        ? 'Due amount'
                                        : 'Return'}{' '}
                                </span>
                                <span
                                    className={`${Number(cashBack) < 0 ? 'text-red-500' : 'text-green-800'} text-[26px] font-semibold`}
                                >
                                    {formatCurrencyInBDT(cashBack)}
                                </span>{' '}
                            </li>
                            {/* <li className="flex items-center justify-between font-semibold">
                <span className="text-muted-foreground text-[16px]">Paid Amount</span>
                {paidEditable ? (
                  <input
                    type="number"
                    value={paidValue}
                    onChange={handlePaidChange}
                    onBlur={handlePaidBlur}
                    onKeyPress={handlePaidKeyPress}
                    className="max-w-[80px]  text-[22px] text-primary"
                  />
                ) : (
                  <span
                    className=" text-[22px] text-primary"
                    onClick={handlePaidClick}
                  >
                    {paidValue}.00 Tk
                  </span>
                )}
              </li> */}
                        </ul>
                    </div>

                    <div className="mt-5">
                        <span className="mb-2 text-[16px] text-muted-foreground">
                            Payment Method
                        </span>
                        <RadioGroup
                            defaultValue={selectedPaymentMethod}
                            onValueChange={setSelectedPaymentMethod}
                            className="grid grid-cols-3 gap-3"
                        >
                            {PAYMENT_METHODS.map((method) => {
                                const Icon = method.icon;
                                return (
                                    <div key={method.id} className="relative">
                                        <RadioGroupItem
                                            value={method.id}
                                            id={method.id}
                                            className="peer sr-only"
                                        />
                                        <Label
                                            htmlFor={method.id}
                                            className="peer-data-[state=checked]:bg-primary/5 flex h-16 w-16 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-muted bg-background transition-all hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:text-primary"
                                        >
                                            <Icon className="mb-2 h-4 w-4" />
                                            <span className="text-center text-xs font-medium">
                                                {method.name}
                                            </span>
                                        </Label>
                                    </div>
                                );
                            })}
                        </RadioGroup>
                        {selectedPaymentMethod !== PaymentMethod.CASH && (
                            <div className="mt-2">
                                <span className="text-[16px] text-muted-foreground">
                                    Transaction ID
                                </span>
                                <Input
                                    type="text"
                                    placeholder="Enter transaction ID"
                                    value={transactionId}
                                    onChange={(e) =>
                                        setTransactionId(e.target.value)
                                    }
                                    className="border-b-2 border-blue-500 focus:outline-none"
                                />
                            </div>
                        )}
                    </div>

                    <div
                        data-orientation="horizontal"
                        role="none"
                        className="my-4 h-[1px] w-full shrink-0 bg-border"
                    ></div>

                    {renderCustomerSelection(customerType)}
                </div>
                <Button
                    type="button"
                    disabled={!cartDetails.total_price}
                    onClick={() => {
                        handleSubmitOrder(Number(paidValue));
                    }}
                    className="mt-5 w-full"
                >
                    Sale & Print
                </Button>
            </Card>
        </>
    );
}
interface EditableFieldProps {
    label: string;
    value: number;
    onChange: (value: number) => void;
    onBlur: () => void;
    inputRef: any;
}

const EditableField = ({
    value,
    onChange,
    onBlur,
    inputRef,
}: EditableFieldProps) => {
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            setIsEditing(false);
            onBlur();
        }
    };

    return (
        <div className="flex justify-between">
            <input
                ref={inputRef}
                type="number"
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                onBlur={() => {
                    setIsEditing(false);
                    onBlur();
                }}
                onFocus={(e) => e.target.select()}
                autoFocus
                onKeyDown={handleKeyDown}
                className="max-w-[80px] border-b-2 border-blue-500 text-right text-[26px] text-primary focus:outline-none"
            />
        </div>
    );
};

const CustomerInfo = ({
    changeSelectedCustomer,
    singleCustomerDetails,
    selectedCustomerId,
}: any) => {
    const { openModal } = useModal();

    return (
        <div className="grid gap-3">
            <div className="flex items-center justify-between font-semibold">
                <span className="text-[22px]">Customer Info</span>
                <Button
                    onClick={() => openModal(MODAL_ROUTE.CREATE_CUSTOMER)}
                    type="button"
                    variant="secondary"
                    size={'sm'}
                >
                    {' '}
                    <FaPlus size={12} /> Add New
                </Button>
            </div>
            <dl className="grid gap-3">
                <div className="flex items-center justify-between gap-3">
                    <dt className="text-[16px] text-muted-foreground">
                        Customer{' '}
                    </dt>
                    <SaleCustomerAutoComplete
                        singleCustomerDetails={singleCustomerDetails}
                        changeSelectedCustomer={changeSelectedCustomer}
                    />
                </div>
                {singleCustomerDetails.email &&
                    singleCustomerDetails.phone_number &&
                    selectedCustomerId && (
                        <>
                            <div className="flex items-center justify-between">
                                <dt className="text-[16px] text-muted-foreground">
                                    Email
                                </dt>
                                <dd>
                                    <a href="mailto:">
                                        {singleCustomerDetails.email}
                                    </a>
                                </dd>
                            </div>
                            <div className="flex items-center justify-between">
                                <dt className="text-[16px] text-muted-foreground">
                                    Phone
                                </dt>

                                <a href="tel:">
                                    {singleCustomerDetails.phone_number}
                                </a>
                            </div>
                        </>
                    )}
            </dl>
        </div>
    );
};
export default CartAmount;
